import {Actions, createEffect, ofType} from '@ngrx/effects';
import {AuthenticationService} from '@app/core/authentication/authentication.service';
import {
    ActionTypes,
    Login,
    LoginFailure,
    LoginSuccess,
    LoginNoop,
    Logout,
    LogoutSuccess,
    LogoutToClientSelectSuccess,
    SupportLogin,
    SupportLogout,
    SupportLogoutSuccess,
    LoginWith2FARequired,
    LoginWith2FA
} from '@app/core/authentication/authentication.actions';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {EMPTY, of} from 'rxjs';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {AppModuleRoutes} from '@app/app-routes';
import {environment} from '../../../environments/environment';
import { AuthenticationMethod } from '@app/shared/models/client-user';

@Injectable()
export class AuthenticationEffects {

    
    Login = createEffect(() => this.actions$
        .pipe(
            ofType(ActionTypes.Login),
            mergeMap((action: Login) => this.authenticationService.login(
                action.payload.username,
                action.payload.password
            )
                .pipe(
                    map(user => {
                        if (user instanceof Object) {
                            // real user logged in:
                            return new LoginSuccess(user);
                        } else {
                            // multi-client login redirect should've happened in the service
                            return new LoginNoop(user);
                        }
                    }),
                    catchError(err => {
                        let data = err.error;
                        if (data && data.resultCode === 'MultiFactorAuthenticationRequiredTotp' || data.resultCode === 'MultiFactorAuthenticationRequiredSms') {
                            return of(new LoginWith2FARequired({
                                auth_method: data.resultCode === 'MultiFactorAuthenticationRequiredTotp' ? AuthenticationMethod.TOTP : AuthenticationMethod.SMS,
                                username: action.payload.username,
                                password: action.payload.password,
                                accessToken: data.accessToken
                            }));
                        }
                        return of(new LoginFailure( { error: err }));
                    })
                ))
        ));

    
    LoginWith2FA = createEffect(() => this.actions$
        .pipe(
            ofType(ActionTypes.LoginWith2FA),
            mergeMap((action: LoginWith2FA) => this.authenticationService.loginWithTwoFA(
                action.payload.username,
                action.payload.code,
                action.payload.token
            )
                .pipe(
                    map(user => {
                        if (user instanceof Object) {
                            // real user logged in:
                            return new LoginSuccess(user);
                        } else {
                            // multi-client login redirect should've happened in the service
                            return new LoginNoop(user);
                        }
                    }),
                    catchError(err => {
                        return of(new LoginFailure( { error: err }));
                    })
                ))
        ));

    
    LoginSuccess = createEffect(() => this.actions$
        .pipe(
            ofType(ActionTypes.LoginSuccess),
            mergeMap(() => this.router.navigateByUrl(AppModuleRoutes.DASHBOARD) ),
        ), { dispatch: false });

    
    SupportLogin = createEffect(() => this.actions$
        .pipe(
            ofType(ActionTypes.SupportLogin),
            mergeMap((action: SupportLogin) => this.authenticationService.loginSupportUser(
                action.payload.supportUser,
                action.payload.supportClient,
                action.payload.supportToken,
                action.payload.supportCountry
            )
                .pipe(
                    map(user => ( new LoginSuccess(user) )),
                    catchError(err => {
                        return of(new LoginFailure( { error: err }));
                    })
                ))
        ));

    
    AutoClientLogin = createEffect(() => this.actions$
        .pipe(
            ofType(ActionTypes.AutoClientLogin),
            mergeMap((action: Login) => this.authenticationService.loginToClient(
                action.payload.autoLogin,
                action.payload.clientId,
                action.payload.username
            )
                .pipe(
                    map(user => {
                        return new LoginSuccess(user);
                    }),
                    catchError(err => {
                        return of(new LoginFailure( { error: err }));
                    })
                ))
        ));

    
    Logout = createEffect(() => this.actions$
        .pipe(
            ofType(ActionTypes.Logout),
            mergeMap((action: Logout) => this.authenticationService.logoutUser()
                .pipe(
                    map(user => ( new LogoutSuccess(user) )),
                    catchError(() => EMPTY)
                ))
        ));

    
    LogoutToClientSelect = createEffect(() => this.actions$
        .pipe(
            ofType(ActionTypes.LogoutToClientSelect),
            mergeMap((action: Logout) => this.authenticationService.logoutUser()
                .pipe(
                    map(result => ( new LogoutToClientSelectSuccess(result) )),
                    catchError(() => EMPTY)
                ))
        ));

    
    LogoutToClientSelectSuccess = createEffect(() => this.actions$
        .pipe(
            ofType(ActionTypes.LogoutToClientSelectSuccess),
            mergeMap((result: LogoutToClientSelectSuccess) => {
                this.authenticationService.onLogoutSuccess();

                const sessionUser = encodeURIComponent(JSON.stringify(result.payload.user));
                const sessionClients = encodeURIComponent(JSON.stringify(result.payload.potential_clients));
                const sessionToken = result.payload.accessToken;
                
                // DEV:
                // const multiClientHost = 'http://dynarex.local';
                const multiClientHost = environment.clientSelectBaseUrl;
                const clientSelectURL = multiClientHost + `/auth/?clientSelectUser=${sessionUser}&clientSelectToken=${sessionToken}&clientSelectClients=${sessionClients}`;
                return window.location.href = clientSelectURL;
            })
        ), { dispatch: false });

    
    LogoutSuccess = createEffect(() => this.actions$
        .pipe(
            ofType(ActionTypes.LogoutSuccess),
            mergeMap(() => {
                this.authenticationService.onLogoutSuccess();
                return this.router.navigateByUrl(AppModuleRoutes.AUTH);
            })
        ), { dispatch: false });



    
    SupportLogout = createEffect(() => this.actions$
        .pipe(
            ofType(ActionTypes.SupportLogout),
            mergeMap((action: SupportLogout) => this.authenticationService.logoutSupportUser()
                .pipe(
                    map(result => ( new SupportLogoutSuccess(result))),
                    catchError(() => EMPTY)
                ))
        ));

    
    SupportLogoutSuccess = createEffect(() => this.actions$
        .pipe(
            ofType(ActionTypes.SupportLogoutSuccess),
            mergeMap((result: SupportLogoutSuccess) => {
                const country = encodeURIComponent(JSON.stringify(this.authenticationService.currentUserValue.country));
                const supportOrigin = this.authenticationService.currentUserValue.user.support_origin;
                this.authenticationService.onLogoutSuccess();
                const platform = result.payload.platform;
                const host = (environment.localhostOverrides && environment.localhostOverrides[platform]) || platform;
                const user = encodeURIComponent(JSON.stringify(result.payload.user));
                const token = result.payload.accessToken;
                const memberUrl = `${host}/member/auth/?sessionUser=${user}&sessionCountry=${country}&supportOrigin=${supportOrigin}&sessionToken=${token}`;
                return window.location.href = memberUrl;
            }),
        ), { dispatch: false });

    // @Effect({dispatch: true})
    // SessionRestore$ = this.actions$
    //     .pipe(
    //         ofType(ActionTypes.SessionRestore),
    //         mergeMap((action: Login) => this.authenticationService.restoreSession(
    //             action.payload.username,
    //             action.payload.password
    //         )
    //             .pipe(
    //                 map(user => ( new LoginSuccess(user) )),
    //                 catchError(() => EMPTY)
    //             ))
    //     );

    constructor(
        private actions$: Actions,
        private authenticationService: AuthenticationService,
        private router: Router,
    ) {}

}
